import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components as AdminComponents } from "@njbsoft/types/admin";
import {
  useAdminDeviceInstallations,
  type AdminDeviceInstallationsParams,
  type AdminDeviceInstallationSortBy,
} from "@/hooks/use-admin-device-installations";
import { useAdminOrgDetail } from "@/hooks/use-admin-orgs";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";
import { useAdminDeviceInstallationsColumns } from "@/components/device-installations/admin-device-installations-columns";
import {
  AdminDeviceInstallationsFilters,
  type AdminDeviceInstallationsFilterValues,
} from "@/components/device-installations/admin-device-installations-filters";

type AdminDeviceInstallationListItem =
  AdminComponents["schemas"]["AdminDeviceInstallationListItem"];

export const Route = createFileRoute("/_admin/device-installations/")({
  validateSearch: (search: Record<string, unknown>) => ({
    org_id: typeof search.org_id === "string" ? search.org_id : undefined,
  }),
  component: AdminDeviceInstallationsListPage,
});

const PAGE_SIZE = 20;

function AdminDeviceInstallationsListPage() {
  const { t } = useTranslation(["installations", "admin"]);
  const router = useRouter();
  const navigate = useNavigate({ from: Route.fullPath });
  const { org_id: urlOrgId } = Route.useSearch();

  const [filters, setFilters] = useState<AdminDeviceInstallationsFilterValues>(() =>
    urlOrgId ? { org_id: urlOrgId } : {},
  );
  // The picked org's zone dates each of ITS rows' Added the way that org's staff
  // see it; a row of any other org prints the dash.
  const { data: org } = useAdminOrgDetail(filters.org_id ?? "");
  const columns = useAdminDeviceInstallationsColumns(org);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);

  useEffect(() => {
    if ((filters.org_id ?? undefined) !== urlOrgId) {
      void navigate({
        search: (prev: Record<string, unknown>) => ({ ...prev, org_id: filters.org_id }),
        replace: true,
      });
    }
  }, [filters.org_id, urlOrgId, navigate]);

  useEffect(() => {
    setPage(1);
  }, [filters, sorting]);

  const firstSort = sorting[0];
  const sortBy = firstSort?.id as AdminDeviceInstallationSortBy | undefined;
  const sortDir = firstSort ? (firstSort.desc ? "desc" : "asc") : undefined;

  const params: AdminDeviceInstallationsParams = useMemo(() => {
    const p: AdminDeviceInstallationsParams = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (filters.org_id) p.org_id = filters.org_id;
    if (filters.device_category) p.device_category = filters.device_category;
    if (filters.device_status) p.device_status = filters.device_status;
    if (sortBy) p.sort_by = sortBy;
    if (sortDir) p.sort_dir = sortDir;
    return p;
  }, [filters.org_id, filters.device_category, filters.device_status, sortBy, sortDir, page]);

  const { data, isLoading, isError, error } = useAdminDeviceInstallations(params);
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  function handleRowClick(row: AdminDeviceInstallationListItem) {
    router.history.push(`/device-installations/${row.id}`);
  }

  const noOrgPicked = !filters.org_id;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-slate-900">{t("installations:title")}</h1>

      <AdminDeviceInstallationsFilters filters={filters} onChange={setFilters} />

      {noOrgPicked ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
          <h2 className="text-base font-semibold text-slate-700">
            {t("admin:deviceInstallation.list.pickOrgTitle")}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {t("admin:deviceInstallation.list.pickOrgDescription")}
          </p>
        </div>
      ) : isError ? (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          {(error as Error)?.message ?? t("installations:empty.title")}
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data?.items ?? []}
            sorting={sorting}
            onSortingChange={setSorting}
            isLoading={isLoading}
            emptyMessage={t("installations:empty.title")}
            onRowClick={handleRowClick}
          />

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={data?.total ?? 0}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
